import { useEffect, useRef, useState } from "react";
import { api, API, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { inr, fmtDate } from "@/lib/format";
import { printInvoice, printGstr1Register, printGstr3bSummary, printGstr1FyRegister, printAnnualReport } from "@/lib/print";
import { Printer, Eye, Paperclip, FileDown, Loader2, Calculator, CalendarRange, BookOpenCheck } from "lucide-react";
import { toast } from "sonner";

const stripInv = (v) => String(v || "").replace(/^INV[-\s]?/i, "") || "—";
const ackUrl = (r) => `${API}/files/${r.gstr1_ack_path}?auth=${localStorage.getItem("ntc_token")}`;

function AckCell({ row, onDone }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/invoices/${row.id}/gstr1-ack`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("GSTR-1 filed copy uploaded");
      onDone();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Upload failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex items-center justify-end gap-1">
      <input ref={inputRef} type="file" accept=".jpg,.jpeg,.png,.webp,.pdf" className="hidden" onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} data-testid={`gstr1-file-input-${row.id}`} />
      {row.gstr1_ack_path && (
        <a href={ackUrl(row)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600 hover:underline" title={row.gstr1_ack_filename || "View GSTR-1 filed copy"} data-testid="view-gstr1-ack-link">
          <Eye className="h-3.5 w-3.5" /> View
        </a>
      )}
      <Button variant="ghost" size="icon" className={`h-7 w-7 ${row.gstr1_ack_path ? "text-slate-400" : "text-emerald-600"}`} title={row.gstr1_ack_path ? "Replace filed copy" : "Upload GSTR-1 filed copy / acknowledgement"} disabled={busy} onClick={() => inputRef.current?.click()} data-testid="upload-gstr1-ack-btn">
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Paperclip className="h-3.5 w-3.5" />}
      </Button>
    </div>
  );
}

export default function Gstr1Dialog({ open, onClose, rows, custFor, onChanged }) {
  const bulkRef = useRef(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [view, setView] = useState("monthly");
  const [fy, setFy] = useState(() => {
    const d = new Date();
    return d.getMonth() + 1 >= 4 ? d.getFullYear() : d.getFullYear() - 1;
  });

  const enrich = (r) => ({
    ...r,
    _gstin: custFor(r)?.gstin || "",
    _bill_no: stripInv(r.invoice_no),
    _taxable: (Number(r.total) || 0) - (Number(r.tax_amount) || 0),
  });
  const [selIds, setSelIds] = useState(() => new Set());
  const toggleSel = (id) =>
    setSelIds((s) => {
      const n = new Set(s);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  const list = (rows || []).filter((r) => (r.issue_date || "").startsWith(month)).map(enrich).sort((a, b) => (a.issue_date || "").localeCompare(b.issue_date || ""));
  const selected = list.filter((r) => selIds.has(r.id));
  const target = selected.length ? selected : list;
  const allSelected = list.length > 0 && selected.length === list.length;
  const toggleAll = () => setSelIds(allSelected ? new Set() : new Set(list.map((r) => r.id)));
  const calcHsn = (arr) =>
    Object.values(
      arr.reduce((m, r) => {
        const key = r.rcm ? "RCM" : String(r.tax_percent || 0);
        const a = (m[key] = m[key] || { sac: "996511", desc: "Goods Transport Agency (GTA) services", uqc: "OTH", qty: 0, rate: r.rcm ? "RCM" : `${r.tax_percent || 0}%`, taxable: 0, tax: 0 });
        a.qty += 1;
        a.taxable += r._taxable;
        a.tax += Number(r.tax_amount) || 0;
        return m;
      }, {})
    );
  const tot = list.reduce((a, r) => ({ taxable: a.taxable + r._taxable, tax: a.tax + (Number(r.tax_amount) || 0), total: a.total + (Number(r.total) || 0) }), { taxable: 0, tax: 0, total: 0 });
  const monthLabel = month ? new Date(month + "-01").toLocaleDateString("en-GB", { month: "long", year: "numeric" }) : "";

  const fwdList = list.filter((r) => !r.rcm);
  const rcmList = list.filter((r) => r.rcm);
  const sumUp = (arr) => arr.reduce((a, r) => ({ count: a.count + 1, taxable: a.taxable + r._taxable, tax: a.tax + (Number(r.tax_amount) || 0) }), { count: 0, taxable: 0, tax: 0 });
  const fwd = sumUp(fwdList);
  const rcm = sumUp(rcmList);
  const hsn = calcHsn(list);

  const fyLabel = `FY ${fy}-${String(fy + 1).slice(2)}`;
  const fyYears = (() => {
    const ys = new Set([fy]);
    (rows || []).forEach((r) => {
      const d = r.issue_date || "";
      if (d) ys.add(Number(d.slice(0, 4)) - (Number(d.slice(5, 7)) < 4 ? 1 : 0));
    });
    return [...ys].sort((a, b) => b - a);
  })();
  const fyMonths = Array.from({ length: 12 }, (_, i) => {
    const y = i < 9 ? fy : fy + 1;
    const m = ((i + 3) % 12) + 1;
    const key = `${y}-${String(m).padStart(2, "0")}`;
    const bills = (rows || []).filter((r) => (r.issue_date || "").startsWith(key));
    return {
      key,
      label: new Date(key + "-01").toLocaleDateString("en-GB", { month: "short", year: "numeric" }),
      count: bills.length,
      taxable: bills.reduce((a, r) => a + (Number(r.total) || 0) - (Number(r.tax_amount) || 0), 0),
      tax: bills.reduce((a, r) => a + (Number(r.tax_amount) || 0), 0),
      total: bills.reduce((a, r) => a + (Number(r.total) || 0), 0),
      filed: bills.filter((r) => r.gstr1_ack_path).length,
    };
  });
  const fyTot = fyMonths.reduce((a, m) => ({ count: a.count + m.count, taxable: a.taxable + m.taxable, tax: a.tax + m.tax, total: a.total + m.total, filed: a.filed + m.filed }), { count: 0, taxable: 0, tax: 0, total: 0, filed: 0 });

  const [annualAcks, setAnnualAcks] = useState({});
  const [ackBusy, setAckBusy] = useState("");
  const annualRefs = useRef({});
  useEffect(() => {
    if (!open) return;
    api.get("/annual-reports").then((r) => {
      const m = {};
      (r.data || []).forEach((a) => { m[a.fy] = a; });
      setAnnualAcks(m);
    }).catch(() => {});
  }, [open]);
  const curFy = (() => { const d = new Date(); return d.getMonth() + 1 >= 4 ? d.getFullYear() : d.getFullYear() - 1; })();
  const annualCols = [...new Set([...fyYears, curFy, curFy + 1])].sort((a, b) => a - b).map((y) => {
    const inFy = (d) => { if (!d) return false; const yr = Number(d.slice(0, 4)), m = Number(d.slice(5, 7)); return (m >= 4 ? yr : yr - 1) === y; };
    const bills = (rows || []).filter((r) => inFy(r.issue_date));
    return {
      y,
      key: `${y}-${String(y + 1).slice(2)}`,
      label: `FY ${y}-${y + 1}`,
      count: bills.length,
      taxable: bills.reduce((a, r) => a + (Number(r.total) || 0) - (Number(r.tax_amount) || 0), 0),
      tax: bills.reduce((a, r) => a + (Number(r.tax_amount) || 0), 0),
      total: bills.reduce((a, r) => a + (Number(r.total) || 0), 0),
      filed: bills.filter((r) => r.gstr1_ack_path).length,
    };
  });
  const uploadAnnual = async (fyKey, file) => {
    if (!file) return;
    setAckBusy(fyKey);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/annual-reports/${fyKey}/ack`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Annual report copy uploaded for FY ${fyKey}`);
      const r = await api.get("/annual-reports");
      const m = {};
      (r.data || []).forEach((a) => { m[a.fy] = a; });
      setAnnualAcks(m);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Upload failed");
    } finally {
      setAckBusy("");
    }
  };

  const downloadFyExcel = () => {
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const lines = [
      ["Month", "Bills", "Taxable Value", "Tax Amount", "Invoice Value", "Filed Copies"].map(esc).join(","),
      ...fyMonths.map((m) => [m.label, m.count, m.taxable.toFixed(2), m.tax.toFixed(2), m.total.toFixed(2), `${m.filed} / ${m.count}`].map(esc).join(",")),
      [`TOTAL — ${fyLabel}`, fyTot.count, fyTot.taxable.toFixed(2), fyTot.tax.toFixed(2), fyTot.total.toFixed(2), `${fyTot.filed} / ${fyTot.count}`].map(esc).join(","),
    ];
    const blob = new Blob(["\ufeff" + lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `ntc-gstr1-fy-${fy}-${String(fy + 1).slice(2)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const downloadExcel = () => {
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const ttot = target.reduce((a, r) => ({ taxable: a.taxable + r._taxable, tax: a.tax + (Number(r.tax_amount) || 0), total: a.total + (Number(r.total) || 0) }), { taxable: 0, tax: 0, total: 0 });
    const lines = [
      ["Sl", "Recipient GSTIN", "Bill No.", "Bill Date", "Customer", "From", "Destination", "Taxable Value", "GST %", "Tax Amount", "Invoice Value", "RCM", "Filed Copy"].map(esc).join(","),
      ...target.map((r, i) => [i + 1, r._gstin, r._bill_no, r.issue_date ? fmtDate(r.issue_date) : "", r.customer_name || "", r.from_place || "", r.destination || "", r._taxable.toFixed(2), r.rcm ? "RCM" : r.tax_percent || 0, (Number(r.tax_amount) || 0).toFixed(2), (Number(r.total) || 0).toFixed(2), r.rcm ? "Yes" : "No", r.gstr1_ack_path ? "Yes" : "No"].map(esc).join(",")),
      ["", "", "", "", "", "", "TOTAL", ttot.taxable.toFixed(2), "", ttot.tax.toFixed(2), ttot.total.toFixed(2), "", ""].map(esc).join(","),
    ];
    const blob = new Blob(["\ufeff" + lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `ntc-gstr1-${month}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const downloadAnnualExcel = () => {
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const row = (label, fn) => [label, ...annualCols.map(fn)].map(esc).join(",");
    const lines = [
      ["Particulars", ...annualCols.map((c) => c.label)].map(esc).join(","),
      row("No. of Bills", (c) => c.count),
      row("Taxable Value", (c) => c.taxable.toFixed(2)),
      row("Tax Amount (GST)", (c) => c.tax.toFixed(2)),
      row("Invoice Value", (c) => c.total.toFixed(2)),
      row("GSTR-1 Filed Copies", (c) => (c.count ? `${c.filed} / ${c.count}` : "")),
      row("Annual Return Copy", (c) => (annualAcks[c.key] ? "Uploaded" : "Pending")),
    ];
    const blob = new Blob(["\ufeff" + lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "ntc-gst-annual-report.csv";
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const bulkUpload = async (file) => {
    if (!file || !selected.length) return;
    setBulkBusy(true);
    try {
      const fd = new FormData();
      fd.append("invoice_ids", selected.map((r) => r.id).join(","));
      fd.append("file", file);
      const res = await api.post("/invoices/gstr1-ack-bulk", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(res.data?.message || "GSTR-1 copy applied");
      setSelIds(new Set());
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Upload failed");
    } finally {
      setBulkBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[1150px]" data-testid="gstr1-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">GSTR-1 — B2B Outward Supplies</DialogTitle>
        </DialogHeader>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex rounded-md border border-border p-0.5">
            <Button variant={view === "monthly" ? "secondary" : "ghost"} size="sm" className="h-7 text-xs" onClick={() => setView("monthly")} data-testid="gstr1-view-monthly">Monthly</Button>
            <Button variant={view === "yearly" ? "secondary" : "ghost"} size="sm" className="h-7 text-xs" onClick={() => setView("yearly")} data-testid="gstr1-view-yearly"><CalendarRange className="mr-1 h-3.5 w-3.5" /> Yearly</Button>
            <Button variant={view === "annual" ? "secondary" : "ghost"} size="sm" className="h-7 text-xs" onClick={() => setView("annual")} data-testid="gstr1-view-annual"><BookOpenCheck className="mr-1 h-3.5 w-3.5" /> Annual Report</Button>
          </div>
          {view === "monthly" ? (
            <>
              <Input type="month" className="w-[170px]" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="gstr1-month-input" />
              <span className="text-xs text-muted-foreground">{list.length} bill{list.length === 1 ? "" : "s"} in {monthLabel}{selected.length > 0 && <span className="ml-1 font-semibold text-primary" data-testid="gstr1-selected-count">· {selected.length} selected</span>}</span>
              <div className="ml-auto flex gap-2">
                <input ref={bulkRef} type="file" accept=".jpg,.jpeg,.png,.webp,.pdf" className="hidden" onChange={(e) => { bulkUpload(e.target.files?.[0]); e.target.value = ""; }} data-testid="gstr1-bulk-file-input" />
                {selected.length > 0 && (
                  <Button variant="outline" size="sm" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" disabled={bulkBusy} onClick={() => bulkRef.current?.click()} data-testid="gstr1-bulk-ack-btn">
                    {bulkBusy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Paperclip className="mr-1.5 h-4 w-4" />} Upload Ack ({selected.length})
                  </Button>
                )}
                <Button variant="outline" size="sm" onClick={() => printGstr1Register(target, { monthLabel, hsn: calcHsn(target) })} disabled={!list.length} data-testid="gstr1-print-btn">
                  <Printer className="mr-1.5 h-4 w-4" /> Print{selected.length > 0 ? ` (${selected.length})` : ""}
                </Button>
                <Button variant="outline" size="sm" onClick={() => printGstr3bSummary({ monthLabel, fwd, rcm, rcmRows: rcmList })} disabled={!list.length} data-testid="gstr3b-print-btn">
                  <Calculator className="mr-1.5 h-4 w-4" /> GSTR-3B
                </Button>
                <Button variant="outline" size="sm" onClick={downloadExcel} disabled={!list.length} data-testid="gstr1-excel-btn">
                  <FileDown className="mr-1.5 h-4 w-4" /> Excel{selected.length > 0 ? ` (${selected.length})` : ""}
                </Button>
              </div>
            </>
          ) : view === "yearly" ? (
            <>
              <select className="h-9 rounded-md border border-input bg-background px-3 text-sm" value={fy} onChange={(e) => setFy(Number(e.target.value))} data-testid="gstr1-fy-select">
                {fyYears.map((y) => (
                  <option key={y} value={y}>FY {y}-{String(y + 1).slice(2)}</option>
                ))}
              </select>
              <span className="text-xs text-muted-foreground">{fyTot.count} bill{fyTot.count === 1 ? "" : "s"} in {fyLabel}</span>
              <div className="ml-auto flex gap-2">
                <Button variant="outline" size="sm" onClick={() => printGstr1FyRegister(fyMonths, { fyLabel, tot: fyTot })} disabled={!fyTot.count} data-testid="gstr1-fy-print-btn">
                  <Printer className="mr-1.5 h-4 w-4" /> Print FY Register
                </Button>
                <Button variant="outline" size="sm" onClick={downloadFyExcel} disabled={!fyTot.count} data-testid="gstr1-fy-excel-btn">
                  <FileDown className="mr-1.5 h-4 w-4" /> Excel
                </Button>
              </div>
            </>
          ) : (
            <>
              <span className="text-xs text-muted-foreground">Financial year wise summary (April – March)</span>
              <div className="ml-auto flex gap-2">
                <Button variant="outline" size="sm" onClick={() => printAnnualReport(annualCols.map((c) => ({ ...c, ack: !!annualAcks[c.key] })))} data-testid="annual-report-print-btn">
                  <Printer className="mr-1.5 h-4 w-4" /> Print
                </Button>
                <Button variant="outline" size="sm" onClick={downloadAnnualExcel} data-testid="annual-report-excel-btn">
                  <FileDown className="mr-1.5 h-4 w-4" /> Excel
                </Button>
              </div>
            </>
          )}
        </div>
        {view === "annual" && (
          <Table data-testid="annual-report-table">
            <TableHeader>
              <TableRow className="bg-slate-50 hover:bg-slate-50">
                <TableHead className="text-[11px] uppercase tracking-wide text-slate-500">Particulars</TableHead>
                {annualCols.map((c) => (
                  <TableHead key={c.key} className="text-right text-[11px] uppercase tracking-wide text-slate-500" data-testid={`annual-col-${c.key}`}>{c.label}</TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {[
                ["No. of Bills", (c) => c.count || "—"],
                ["Taxable Value", (c) => (c.count ? inr(c.taxable) : "—")],
                ["Tax Amount (GST)", (c) => (c.count ? inr(c.tax) : "—")],
                ["Invoice Value", (c) => (c.count ? inr(c.total) : "—")],
                ["GSTR-1 Filed Copies", (c) => (c.count ? `${c.filed} / ${c.count}` : "—")],
              ].map(([label, fn]) => (
                <TableRow key={label} data-testid="annual-report-row">
                  <TableCell className="text-sm font-medium">{label}</TableCell>
                  {annualCols.map((c) => (
                    <TableCell key={c.key} className="text-right text-sm tabular-nums">{fn(c)}</TableCell>
                  ))}
                </TableRow>
              ))}
              <TableRow className="bg-slate-50/60" data-testid="annual-ack-row">
                <TableCell className="text-sm font-medium">Annual Return Copy</TableCell>
                {annualCols.map((c) => {
                  const ack = annualAcks[c.key];
                  return (
                    <TableCell key={c.key} className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <input type="file" accept=".jpg,.jpeg,.png,.webp,.pdf" className="hidden" ref={(el) => { annualRefs.current[c.key] = el; }} onChange={(e) => { uploadAnnual(c.key, e.target.files?.[0]); e.target.value = ""; }} data-testid={`annual-file-input-${c.key}`} />
                        {ack?.ack_path && (
                          <a href={`${API}/files/${ack.ack_path}?auth=${localStorage.getItem("ntc_token")}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600 hover:underline" data-testid={`annual-view-${c.key}`}>
                            <Eye className="h-3.5 w-3.5" /> View
                          </a>
                        )}
                        <Button variant="ghost" size="icon" className={`h-7 w-7 ${ack?.ack_path ? "text-slate-400" : "text-emerald-600"}`} title={ack?.ack_path ? "Replace annual return copy" : "Upload annual return copy"} disabled={ackBusy === c.key} onClick={() => annualRefs.current[c.key]?.click()} data-testid={`annual-upload-${c.key}`}>
                          {ackBusy === c.key ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Paperclip className="h-3.5 w-3.5" />}
                        </Button>
                      </div>
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableBody>
          </Table>
        )}
        {view === "yearly" && (
          <Table data-testid="gstr1-fy-table">
            <TableHeader>
              <TableRow className="bg-slate-50 hover:bg-slate-50">
                {["Month", "Bills", "Taxable Value", "Tax", "Invoice Value", "Filed Copy"].map((h) => (
                  <TableHead key={h} className={`text-[11px] uppercase tracking-wide text-slate-500 ${h === "Month" ? "" : "text-right"}`}>{h}</TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {fyMonths.map((m) => (
                <TableRow key={m.key} className={m.count ? "cursor-pointer hover:bg-blue-50/50" : "text-muted-foreground"} title={m.count ? `Open ${m.label} bill list` : ""} onClick={() => { if (m.count) { setMonth(m.key); setView("monthly"); } }} data-testid="gstr1-fy-row">
                  <TableCell className="text-sm font-medium">{m.label}</TableCell>
                  <TableCell className="text-right text-sm tabular-nums">{m.count || "—"}</TableCell>
                  <TableCell className="text-right text-sm tabular-nums">{m.count ? inr(m.taxable) : "—"}</TableCell>
                  <TableCell className="text-right text-sm tabular-nums">{m.count ? inr(m.tax) : "—"}</TableCell>
                  <TableCell className="text-right text-sm tabular-nums">{m.count ? inr(m.total) : "—"}</TableCell>
                  <TableCell className="text-right text-sm">
                    {m.count ? (
                      <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${m.filed === m.count ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>{m.filed} / {m.count}</span>
                    ) : "—"}
                  </TableCell>
                </TableRow>
              ))}
              <TableRow className="bg-slate-50 font-semibold hover:bg-slate-50" data-testid="gstr1-fy-totals-row">
                <TableCell className="text-sm">TOTAL — {fyLabel}</TableCell>
                <TableCell className="text-right text-sm tabular-nums">{fyTot.count}</TableCell>
                <TableCell className="text-right text-sm tabular-nums">{inr(fyTot.taxable)}</TableCell>
                <TableCell className="text-right text-sm tabular-nums">{inr(fyTot.tax)}</TableCell>
                <TableCell className="text-right text-sm tabular-nums">{inr(fyTot.total)}</TableCell>
                <TableCell className="text-right text-sm">{fyTot.filed} / {fyTot.count}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        )}
        {view === "monthly" && (
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50 hover:bg-slate-50">
              <TableHead className="w-8">
                <input type="checkbox" className="h-4 w-4 accent-blue-600" checked={allSelected} onChange={toggleAll} title="Select all" data-testid="gstr1-select-all" />
              </TableHead>
              {["Sl", "GSTIN", "Bill No.", "Date", "Customer", "From", "Destination", "Taxable", "GST %", "Tax", "Total"].map((h) => (
                <TableHead key={h} className={`text-[11px] uppercase tracking-wide text-slate-500 ${["Taxable", "Tax", "Total"].includes(h) ? "text-right" : ""}`}>{h}</TableHead>
              ))}
              <TableHead className="text-right text-[11px] uppercase tracking-wide text-slate-500">Print / Upload</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {list.length === 0 && (
              <TableRow><TableCell colSpan={13} className="py-10 text-center text-muted-foreground" data-testid="gstr1-empty">No bills in this month.</TableCell></TableRow>
            )}
            {list.map((r, i) => (
              <TableRow key={r.id} data-testid="gstr1-row" className={selIds.has(r.id) ? "bg-blue-50/40" : ""}>
                <TableCell>
                  <input type="checkbox" className="h-4 w-4 accent-blue-600" checked={selIds.has(r.id)} onChange={() => toggleSel(r.id)} data-testid="gstr1-row-select" />
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">{i + 1}</TableCell>
                <TableCell className="text-xs">{r._gstin || <span className="text-muted-foreground">—</span>}</TableCell>
                <TableCell className="text-sm font-medium">{r._bill_no}</TableCell>
                <TableCell className="text-sm">{fmtDate(r.issue_date)}</TableCell>
                <TableCell className="text-sm">{r.customer_name || "—"}</TableCell>
                <TableCell className="text-sm">{r.from_place || "—"}</TableCell>
                <TableCell className="text-sm">{r.destination || "—"}</TableCell>
                <TableCell className="text-right text-sm tabular-nums">{inr(r._taxable)}</TableCell>
                <TableCell className="text-sm">{r.rcm ? <span className="rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700">RCM</span> : `${r.tax_percent || 0}%`}</TableCell>
                <TableCell className="text-right text-sm tabular-nums">{inr(r.tax_amount || 0)}</TableCell>
                <TableCell className="text-right text-sm font-medium tabular-nums">{inr(r.total || 0)}</TableCell>
                <TableCell>
                  <div className="flex items-center justify-end gap-1">
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-primary" title="Print bill" onClick={() => printInvoice(r, custFor(r))} data-testid="gstr1-print-bill-btn">
                      <Printer className="h-3.5 w-3.5" />
                    </Button>
                    <AckCell row={r} onDone={onChanged} />
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {list.length > 0 && (
              <TableRow className="bg-slate-50 font-semibold hover:bg-slate-50" data-testid="gstr1-totals-row">
                <TableCell colSpan={8} className="text-right text-sm">TOTAL</TableCell>
                <TableCell className="text-right text-sm tabular-nums">{inr(tot.taxable)}</TableCell>
                <TableCell />
                <TableCell className="text-right text-sm tabular-nums">{inr(tot.tax)}</TableCell>
                <TableCell className="text-right text-sm tabular-nums">{inr(tot.total)}</TableCell>
                <TableCell />
              </TableRow>
            )}
          </TableBody>
        </Table>
        )}
        {view === "monthly" && list.length > 0 && (
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="rounded-md border border-border p-3" data-testid="gstr3b-summary">
              <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">GSTR-3B Summary — {monthLabel}</div>
              <div className="grid gap-1 text-sm">
                <div className="flex justify-between gap-2">
                  <span>3.1(a) Forward charge ({fwd.count} bill{fwd.count === 1 ? "" : "s"})</span>
                  <span className="tabular-nums">{inr(fwd.taxable)} <span className="text-emerald-600">+ {inr(fwd.tax)} tax</span></span>
                </div>
                <div className="flex justify-between gap-2">
                  <span>RCM — tax by recipient ({rcm.count} bill{rcm.count === 1 ? "" : "s"})</span>
                  <span className="tabular-nums">{inr(rcm.taxable)} <span className="text-amber-600">+ ₹0 tax</span></span>
                </div>
                <div className="flex justify-between gap-2 border-t border-border pt-1 font-semibold">
                  <span>Total output tax payable</span>
                  <span className="tabular-nums text-emerald-700" data-testid="gstr3b-output-tax">{inr(fwd.tax)}</span>
                </div>
              </div>
              {rcmList.length > 0 && (
                <div className="mt-2 text-xs text-muted-foreground" data-testid="gstr3b-rcm-list">
                  RCM bills: {rcmList.map((r) => r._bill_no).join(", ")}
                </div>
              )}
            </div>
            <div className="rounded-md border border-border p-3" data-testid="gstr1-hsn-summary">
              <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">HSN / SAC Summary</div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-[10px] uppercase">SAC</TableHead>
                    <TableHead className="text-[10px] uppercase">Rate</TableHead>
                    <TableHead className="text-right text-[10px] uppercase">Bills</TableHead>
                    <TableHead className="text-right text-[10px] uppercase">Taxable</TableHead>
                    <TableHead className="text-right text-[10px] uppercase">Tax</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {hsn.map((h) => (
                    <TableRow key={h.rate} data-testid="hsn-row">
                      <TableCell className="text-xs font-medium">{h.sac}</TableCell>
                      <TableCell className="text-xs">{h.rate}</TableCell>
                      <TableCell className="text-right text-xs tabular-nums">{h.qty}</TableCell>
                      <TableCell className="text-right text-xs tabular-nums">{inr(h.taxable)}</TableCell>
                      <TableCell className="text-right text-xs tabular-nums">{inr(h.tax)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <div className="mt-1 text-[10px] text-muted-foreground">SAC 996511 — Goods Transport Agency (GTA) services · UQC: OTH</div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
